import {memo, useState, useCallback, useMemo} from "react";
import {Badge, Box, Flex, HStack, Table, Text, VStack} from "@chakra-ui/react";
import {SparklesIcon, ChevronDownIcon, ChevronRightIcon} from "@heroicons/react/16/solid";
import {ELC_SCORING_CONFIGS, ELC_EVIDENCE} from "../mockData";
import type {
  MockEvidence,
  MockComplexityAttribute,
  MockComplexityRule,
  MockComplexityTier,
  MockComplexityConfig,
} from "../types";
import type {ElcHeaderProps} from "../types";
import {ElcHeader} from "./ElcHeader";
import {EvidenceDrawer} from "./ProcessEvidenceActivity";
import {AiProvenance} from "./EntitySummary";
import ValueInput from "../../../../../components/ValueInput";

// ---------------------------------------------------------------------------
// Complexity Assessment UI for IT Environment
// ---------------------------------------------------------------------------

function useComplexityState(elcId: string) {
  const config = ELC_SCORING_CONFIGS[elcId] ?? ELC_SCORING_CONFIGS["elc-it-environment"];

  const [values, setValues] = useState<Record<string, number | boolean | string[]>>(() => {
    const init: Record<string, number | boolean | string[]> = {};
    for (const attr of config.attributes) init[attr.id] = attr.initialValue;
    return init;
  });

  const rows = useMemo(
    () =>
      config.attributes.map(attr => {
        const value = values[attr.id];
        const matched = attr.rules
          .map((rule, i) => ({i, rule}))
          .filter(({rule}) => rule.conditions.length > 0 && rule.conditions.every(c => c.evaluate(value)));

        let activeIndices = matched.map(m => m.i);
        if (attr.operator === "First" && activeIndices.length > 1) {
          activeIndices = [activeIndices[0]];
        }

        let rawScore: number | null;
        if (activeIndices.length === 0) {
          rawScore = attr.defaultScore;
        } else if (attr.operator === "Max") {
          rawScore = Math.max(...activeIndices.map(i => attr.rules[i].score));
        } else if (attr.operator === "Sum") {
          rawScore = activeIndices.reduce((s, i) => s + attr.rules[i].score, 0);
        } else {
          rawScore = attr.rules[activeIndices[0]].score;
        }

        const weightedScore = rawScore != null ? rawScore * attr.weight : null;
        return {attr, value, activeIndices, rawScore, weightedScore};
      }),
    [values, config.attributes],
  );

  const totalScore = useMemo(() => rows.reduce((s, r) => s + (r.weightedScore ?? 0), 0), [rows]);

  const effectiveTier = useMemo(() => {
    return (
      config.tiers.find(t => {
        const above = t.lowerBound == null || totalScore >= t.lowerBound;
        const below = t.upperBound == null || totalScore < t.upperBound;
        return above && below;
      }) ?? config.tiers[config.tiers.length - 1]
    );
  }, [totalScore, config.tiers]);

  const updateValue = useCallback((id: string, v: number | boolean | string[]) => {
    setValues(prev => ({...prev, [id]: v}));
  }, []);

  return {config, rows, totalScore, effectiveTier, updateValue};
}

const ComplexitySummaryCard = memo(
  ({
    totalScore,
    tiers,
    effectiveTier,
  }: {
    totalScore: number;
    tiers: MockComplexityTier[];
    effectiveTier: MockComplexityTier;
  }) => (
    <Box border="1px solid {colors.gray.200}" borderRadius="md" bg="white" p={5}>
      <HStack gap={8} align="start">
        {/* Total weighted score */}
        <VStack align="start" gap={1} minW="160px">
          <Text fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase">
            Total Weighted Score
          </Text>
          <Text fontSize="3xl" fontWeight="bold" color="gray.900">
            {totalScore.toLocaleString()}
          </Text>
        </VStack>

        {/* Thresholds */}
        <VStack align="start" gap={1} flex={1}>
          <Text fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase">
            Thresholds
          </Text>
          <HStack gap={2} flexWrap="wrap" mt={1}>
            {tiers.map(tier => {
              const bound =
                tier.upperBound != null
                  ? `< ${tier.upperBound.toLocaleString()}`
                  : `${tier.lowerBound!.toLocaleString()}+`;
              return (
                <HStack key={tier.name} gap={1.5}>
                  <Badge colorPalette={tier.colorPalette} variant="subtle" size="sm">
                    {tier.name}
                  </Badge>
                  <Text fontSize="sm" color="gray.600">
                    {bound}
                  </Text>
                </HStack>
              );
            })}
          </HStack>
        </VStack>

        {/* Complexity level */}
        <VStack align="end" gap={1}>
          <Text fontSize="xs" fontWeight="medium" color="gray.500" textTransform="uppercase">
            Complexity Level
          </Text>
          <Badge colorPalette={effectiveTier.colorPalette} variant="solid" size="lg">
            {effectiveTier.name}
          </Badge>
          <HStack gap={1} mt={1}>
            <Box as={SparklesIcon} boxSize="12px" color="gray.500" />
            <Text fontSize="xs" color="gray.500">
              Generated by AI
            </Text>
          </HStack>
        </VStack>
      </HStack>
    </Box>
  ),
);

const MockRulesExpander = memo(
  ({
    rules,
    activeIndices,
    defaultScore,
  }: {
    rules: MockComplexityRule[];
    activeIndices: number[];
    defaultScore: number | null;
  }) => {
    const [expanded, setExpanded] = useState(false);
    const activeSet = new Set(activeIndices);

    // Summary: show active rule conditions
    const activeSummary = activeIndices.map(i => {
      const rule = rules[i];
      const labels = rule.conditions.map(c => c.label).join(" and ");
      return {labels, score: rule.score};
    });

    const isDefault = activeIndices.length === 0 && defaultScore != null;

    return (
      <VStack align="stretch" gap={1}>
        {/* Active summary */}
        {activeSummary.length > 0 && (
          <HStack gap={1} flexWrap="wrap">
            {activeSummary.map((s, i) => (
              <HStack
                key={i}
                gap={1}
                bg="green.50"
                border="1px solid {colors.green.200}"
                borderRadius="sm"
                px={1.5}
                py={0.5}
              >
                <Text fontSize="xs" color="green.700">
                  {s.labels}
                </Text>
                <Text fontSize="xs" color="green.700" fontWeight="semibold">
                  → {s.score}
                </Text>
              </HStack>
            ))}
          </HStack>
        )}
        {isDefault && (
          <HStack gap={1}>
            <Text fontSize="xs" color="gray.500" fontStyle="italic">
              Default score: {defaultScore}
            </Text>
          </HStack>
        )}

        {/* Toggle */}
        <HStack
          as="button"
          gap={0.5}
          cursor="pointer"
          onClick={() => setExpanded(e => !e)}
          color="gray.500"
          _hover={{color: "gray.700"}}
        >
          <Box as={expanded ? ChevronDownIcon : ChevronRightIcon} boxSize="14px" />
          <Text fontSize="xs">{expanded ? "Hide rules" : "Show rules"}</Text>
        </HStack>

        {/* Expanded rules list */}
        {expanded && (
          <VStack align="stretch" gap={1} pl={2}>
            {rules.map((rule, i) => {
              const isActive = activeSet.has(i);
              const labels = rule.conditions.map(c => c.label).join(" and ");
              return (
                <HStack
                  key={i}
                  gap={2}
                  px={2}
                  py={1}
                  borderRadius="sm"
                  bg={isActive ? "green.50" : "gray.50"}
                  border={isActive ? "1px solid {colors.green.200}" : "1px solid {colors.gray.200}"}
                >
                  <Text fontSize="xs" color={isActive ? "green.700" : "gray.600"} flex={1}>
                    {labels || "Default"}
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold" color={isActive ? "green.700" : "gray.600"}>
                    → {rule.score}
                  </Text>
                </HStack>
              );
            })}
            {defaultScore != null && (
              <HStack
                gap={2}
                px={2}
                py={1}
                borderRadius="sm"
                bg={isDefault ? "green.50" : "gray.50"}
                border={isDefault ? "1px solid {colors.green.200}" : "1px solid {colors.gray.200}"}
              >
                <Text fontSize="xs" color={isDefault ? "green.700" : "gray.600"} flex={1} fontStyle="italic">
                  Default (no rules match)
                </Text>
                <Text fontSize="xs" fontWeight="semibold" color={isDefault ? "green.700" : "gray.600"}>
                  → {defaultScore}
                </Text>
              </HStack>
            )}
          </VStack>
        )}
      </VStack>
    );
  },
);

const ScoreCell = memo(
  ({
    rawScore,
    weight,
    weightedScore,
    activeIndices,
    rules,
    operator,
  }: {
    rawScore: number | null;
    weight: number;
    weightedScore: number | null;
    activeIndices: number[];
    rules: MockComplexityRule[];
    operator: string;
  }) => {
    if (rawScore == null || weightedScore == null) {
      return (
        <Text fontSize="sm" color="gray.400">
          —
        </Text>
      );
    }

    // Simple case: weight=1 and single active rule
    if (weight === 1 && activeIndices.length <= 1) {
      return (
        <Text fontSize="sm" fontWeight="bold">
          {weightedScore}
        </Text>
      );
    }

    // Complex case: show calculation
    let scoreDisplay: string;
    if (activeIndices.length > 1 && operator === "Max") {
      scoreDisplay = `Max(${activeIndices.map(i => rules[i].score).join(", ")})`;
    } else if (activeIndices.length > 1 && operator === "Sum") {
      scoreDisplay = `(${activeIndices.map(i => rules[i].score).join(" + ")})`;
    } else {
      scoreDisplay = String(rawScore);
    }

    return (
      <VStack align="end" gap={0}>
        <HStack gap={1} justify="end">
          <Text fontSize="xs" color="gray.500">
            score
          </Text>
          <Text fontSize="xs" fontWeight="semibold">
            {scoreDisplay}
          </Text>
        </HStack>
        <HStack gap={1} justify="end">
          <Text fontSize="xs" color="gray.500">
            weight
          </Text>
          <Text fontSize="xs" fontWeight="semibold">
            ×{weight}
          </Text>
        </HStack>
        <Box borderTop="1px solid {colors.gray.300}" w="100%" mt={0.5} mb={0.5} />
        <HStack gap={1} justify="end">
          <Text fontSize="xs" color="gray.500">
            total
          </Text>
          <Text fontSize="sm" fontWeight="bold">
            {weightedScore}
          </Text>
        </HStack>
      </VStack>
    );
  },
);

const ComplexityAttributeTable = memo(
  ({
    rows,
    updateValue,
    onCitationClick,
    elcId,
    config,
  }: {
    rows: ReturnType<typeof useComplexityState>["rows"];
    updateValue: (id: string, v: number | boolean | string[]) => void;
    onCitationClick: (ev: MockEvidence) => void;
    elcId: string;
    config: MockComplexityConfig;
  }) => {
    const evidence = useMemo(() => ELC_EVIDENCE[elcId] ?? [], [elcId]);

    // Distribute evidence round-robin across attributes based on sourceCount
    const getAttributeEvidence = useCallback(
      (attr: MockComplexityAttribute): MockEvidence[] => {
        if (evidence.length === 0 || attr.sourceCount === 0) return [];
        const startIdx = config.attributes.indexOf(attr) % evidence.length;
        const result: MockEvidence[] = [];
        for (let i = 0; i < Math.min(attr.sourceCount, evidence.length); i++) {
          result.push(evidence[(startIdx + i) % evidence.length]);
        }
        return result;
      },
      [evidence, config.attributes],
    );

    return (
      <Box
        border="1px solid {colors.gray.200}"
        borderRadius="md"
        bg="white"
        overflow="hidden"
        css={{"& tr:last-child td": {borderBottom: "none"}}}
      >
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row bg="gray.50">
              <Table.ColumnHeader px={6} minW="180px">
                Attribute
              </Table.ColumnHeader>
              <Table.ColumnHeader px={6} minW="200px">
                Value
              </Table.ColumnHeader>
              <Table.ColumnHeader px={6} minW="200px">
                Rules
              </Table.ColumnHeader>
              <Table.ColumnHeader px={6} minW="100px" textAlign="end">
                Score
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map(row => {
              const attrEvidence = getAttributeEvidence(row.attr);
              const valueType =
                row.attr.valueType === "Integer"
                  ? "Integer"
                  : row.attr.valueType === "Boolean"
                    ? "Boolean"
                    : "StringSet";

              return (
                <Table.Row key={row.attr.id} borderTop="1px solid {colors.gray.100}">
                  <Table.Cell verticalAlign="top" pt={3} px={6}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                      {row.attr.name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell verticalAlign="top" pt={2} px={6}>
                    <Box maxW="260px">
                      <ValueInput
                        type={valueType}
                        value={row.value}
                        onChange={v => {
                          if (v != null) updateValue(row.attr.id, v as number | boolean | string[]);
                        }}
                        options={row.attr.options}
                      />
                      <AiProvenance
                        contentKey={`elc-complexity-${row.attr.id}`}
                        meta={{editedBy: "ai", editedAt: new Date()}}
                        citations={attrEvidence}
                        onCitationClick={onCitationClick}
                      />
                    </Box>
                  </Table.Cell>
                  <Table.Cell verticalAlign="top" pt={2} px={6}>
                    <MockRulesExpander
                      rules={row.attr.rules}
                      activeIndices={row.activeIndices}
                      defaultScore={row.attr.defaultScore}
                    />
                  </Table.Cell>
                  <Table.Cell verticalAlign="top" pt={3} textAlign="end" px={6}>
                    <ScoreCell
                      rawScore={row.rawScore}
                      weight={row.attr.weight}
                      weightedScore={row.weightedScore}
                      activeIndices={row.activeIndices}
                      rules={row.attr.rules}
                      operator={row.attr.operator}
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Box>
    );
  },
);

export const ElcComplexity = memo(({headerProps}: {headerProps: ElcHeaderProps}) => {
  const {config, rows, totalScore, effectiveTier, updateValue} = useComplexityState(headerProps.elc.id);
  const [drawerEvidence, setDrawerEvidence] = useState<MockEvidence | null>(null);
  const openDrawer = useCallback((ev: MockEvidence) => setDrawerEvidence(ev), []);
  const closeDrawer = useCallback(() => setDrawerEvidence(null), []);

  return (
    <Flex direction="column" minH={0} flex={1}>
      <ElcHeader {...headerProps} />
      <Box p={6} bg="gray.50" flex={1} overflow="auto">
        <VStack align="stretch" gap={6}>
          <ComplexitySummaryCard totalScore={totalScore} tiers={config.tiers} effectiveTier={effectiveTier} />
          <ComplexityAttributeTable
            rows={rows}
            updateValue={updateValue}
            onCitationClick={openDrawer}
            elcId={headerProps.elc.id}
            config={config}
          />
        </VStack>
      </Box>
      <EvidenceDrawer evidence={drawerEvidence} open={drawerEvidence !== null} onClose={closeDrawer} />
    </Flex>
  );
});
